if (!window.Shopify) window.Shopify = {};

if (!Shopify.formatMoney) {
  Shopify.formatMoney = function (cents, format = "${{amount}}") {
    if (typeof cents === "string") cents = cents.replace(".", "");

    const value = (cents / 100).toFixed(2);

    return format.replace("{{amount}}", value);
  };
}

if (!customElements.get("selling-plan-selector")) {
  class SellingPlanSelector extends HTMLElement {
    constructor() {
      super();
      this.sellingPlanContainer = this.querySelector(".selling-plan");
      this.segmentDiscount = this.dataset.segmentDiscount * 1;

      this.productForm =
        this.closest(".product-section")?.querySelector(
          `#${this.dataset.productForm}`,
        ) || document.getElementById(this.dataset.productForm);

      if (!this.productForm) return;

      this.subscriptionGroup = this.querySelector(
        ".selling-plan__group--subscription",
      );
      this.oneTimeGroup = this.querySelector(".selling-plan__group--one-time");
      this.variantInput = this.productForm.querySelector('[name="id"]');
      this.sellingPlanInput = this.querySelector('[name="selling_plan"]');

      // New select-based purchase option
      this.purchaseOptionSelect = this.querySelector(".purchase-option-select");

      // Existing select(s) for cadence / frequency
      this.planSelects =
        this.sellingPlanContainer?.querySelectorAll("select") || [];

      this.handlePlanSelectChange = this.handlePlanSelectChange.bind(this);
      this.handleRadioChange = this.handleRadioChange.bind(this);
      this.handlePurchaseOptionSelectChange =
        this.handlePurchaseOptionSelectChange.bind(this);

      this.initEvents();
    }

    initEvents() {
      this.planSelects.forEach((select) => {
        select.addEventListener("change", this.handlePlanSelectChange);
      });

      this.querySelectorAll('input[type="radio"]').forEach((radio) => {
        radio.addEventListener("change", this.handleRadioChange);
      });

      if (this.purchaseOptionSelect) {
        this.purchaseOptionSelect.addEventListener(
          "change",
          this.handlePurchaseOptionSelectChange,
        );
      }
    }

    getSelectedVariant() {
      if (!this.variantInput) return null;

      const variantJson = this.productForm.querySelector(
        '[type="application/json"]:not([id$="-variants-metafields"])',
      );
      if (!variantJson) return null;

      return findSelectedVariantData(
        this.variantInput.value * 1,
        JSON.parse(variantJson.textContent),
      );
    }

    getSelectedPlanSelect() {
      return this.sellingPlanContainer?.querySelector("select") || null;
    }

    getCurrentSellingPlanValue() {
      return this.sellingPlanInput?.value || "";
    }

    isOneTimeSelected() {
      // New select-based UI
      if (this.purchaseOptionSelect) {
        return this.purchaseOptionSelect.value === "";
      }

      // Existing radio-based UI
      const checkedRadio = this.querySelector('input[type="radio"]:checked');
      if (!checkedRadio) return true;

      return !!checkedRadio.dataset.oneTime;
    }

    getSelectedSellingPlanId() {
      if (this.isOneTimeSelected()) return "";

      const selectedPlanSelect = this.getSelectedPlanSelect();
      return selectedPlanSelect ? selectedPlanSelect.value : "";
    }

    getSelectedAllocation(selectedVariant, sellingPlanId) {
      if (!selectedVariant || !sellingPlanId) return null;

      return (
        selectedVariant.selling_plan_allocations?.find(
          (allocation) =>
            String(allocation.selling_plan_id) === String(sellingPlanId),
        ) || null
      );
    }

    updateSellingPlanInput() {
      if (!this.sellingPlanInput) return "";

      const sellingPlanId = this.getSelectedSellingPlanId();
      const valueChanged = this.sellingPlanInput.value !== sellingPlanId;
      this.sellingPlanInput.value = sellingPlanId;

      if (valueChanged) {
        this.sellingPlanInput.dispatchEvent(
          new Event("change", { bubbles: true }),
        );
      }

      return sellingPlanId;
    }

    updateFrequencyVisibility() {
      if (!this.purchaseOptionSelect) return;

      const selectedPlanSelect = this.getSelectedPlanSelect();
      if (!selectedPlanSelect) return;

      const wrapper =
        selectedPlanSelect.closest(".selling-plan__group") ||
        selectedPlanSelect.closest(".selling-plan");

      if (!wrapper) return;

      wrapper.style.display =
        this.purchaseOptionSelect.value === "" ? "none" : "";
    }

    publishPriceUpdate() {
      const selectedVariant = this.getSelectedVariant();
      if (!selectedVariant) return;

      const sellingPlanId = this.getCurrentSellingPlanValue();
      const allocation = this.getSelectedAllocation(
        selectedVariant,
        sellingPlanId,
      );

      const productPrice =
        this.productForm.querySelector("product-price") ||
        this.closest(".product-section")?.querySelector("product-price");

      if (!productPrice) return;

      publish(PUB_SUB_EVENTS.onPriceUpdate, {
        target: productPrice,
        data: {
          price: allocation ? allocation.price : selectedVariant.price,
          compareAtPrice: allocation
            ? allocation.compare_at_price
            : selectedVariant.compare_at_price > selectedVariant.price
              ? selectedVariant.compare_at_price
              : null,
          isOnetime: sellingPlanId === "",
          sellingPlanAllocation: allocation,
        },
      });
    }

    publishSellingPlanUpdate(sellingPlan) {
      publish(PUB_SUB_EVENTS.sellingPlanUpdate, {
        data: {
          sellingPlan,
          formId: this.productForm?.id || null,
        },
      });
    }

    handlePlanSelectChange() {
      const sellingPlan = this.updateSellingPlanInput();
      this.publishSellingPlanUpdate(sellingPlan);
      this.publishPriceUpdate();
    }

    handleRadioChange(e) {
      const sellingPlan = this.updateSellingPlanInput();

      this.publishSellingPlanUpdate(sellingPlan);
      this.publishPriceUpdate();
    }

    handlePurchaseOptionSelectChange() {
      this.updateFrequencyVisibility();
      const sellingPlan = this.updateSellingPlanInput();
      this.publishSellingPlanUpdate(sellingPlan);
      this.publishPriceUpdate();
    }

    variantChangedUnsubscriber = undefined;
    connectedCallback() {
      this.variantChangedUnsubscriber = subscribe(
        PUB_SUB_EVENTS.variantChange,
        (event) => {
          if (
            event.target !== this.productForm.querySelector("variant-radios") &&
            event.target !== this.productForm.querySelector("pdp-radios") &&
            event.target !== this.productForm.querySelector("variant-selects")
          ) {
            return;
          }

          if (!event.data.variant?.selling_plan_allocations?.length) return;

          const firstAllocation =
            event.data.variant.selling_plan_allocations[0];

          if (this.subscriptionGroup) {
            const subscriptionPriceEl = this.subscriptionGroup.querySelector(
              ".selling-plan__price",
            );

            if (subscriptionPriceEl) {
              subscriptionPriceEl.innerHTML = Shopify.formatMoney(
                firstAllocation.price,
                "${{amount}}",
              );
            }
          }

          if (this.oneTimeGroup) {
            const finalPrice = this.segmentDiscount
              ? Math.ceil(
                  firstAllocation.compare_at_price -
                    (firstAllocation.compare_at_price * this.segmentDiscount) /
                      100,
                )
              : firstAllocation.compare_at_price;

            const oneTimePriceEl = this.oneTimeGroup.querySelector(
              ".selling-plan__price",
            );

            if (oneTimePriceEl) {
              oneTimePriceEl.innerHTML = Shopify.formatMoney(
                finalPrice,
                "${{amount}}",
              );
            }
          }

          // Keep select UI in sync after variant change
          this.updateFrequencyVisibility();
          this.updateSellingPlanInput();
          this.publishPriceUpdate();
        },
      );

      this.updateFrequencyVisibility();
      this.updateSellingPlanInput();
    }

    disconnectedCallback() {
      if (this.variantChangedUnsubscriber) {
        this.variantChangedUnsubscriber();
      }
    }
  }

  customElements.define("selling-plan-selector", SellingPlanSelector);
}

if (!customElements.get("subscription-terms-sync")) {
  class SubscriptionTermsSync extends HTMLElement {
    constructor() {
      super();
      this.input = this.querySelector(
        '[name="properties[_subscription_terms]"]',
      );
      this.template = this.input?.value || "";
      this.initialSyncAttempts = 0;

      this.handleVariantChange = this.handleVariantChange.bind(this);
      this.handleSellingPlanChange = this.handleSellingPlanChange.bind(this);
    }

    connectedCallback() {
      if (!this.input) return;

      this.form = this.closest("form");

      this.variantChangedUnsubscriber = subscribe(
        PUB_SUB_EVENTS.variantChange,
        this.handleVariantChange,
      );
      this.sellingPlanChangedUnsubscriber = subscribe(
        PUB_SUB_EVENTS.sellingPlanUpdate,
        this.handleSellingPlanChange,
      );

      this.updateInputValue();
    }

    disconnectedCallback() {
      if (this.variantChangedUnsubscriber) {
        this.variantChangedUnsubscriber();
      }

      if (this.sellingPlanChangedUnsubscriber) {
        this.sellingPlanChangedUnsubscriber();
      }
    }

    isVariantEventForThisForm(eventTarget) {
      if (!this.form || !eventTarget) return false;

      return (
        eventTarget === this.form.querySelector("variant-radios") ||
        eventTarget === this.form.querySelector("pdp-radios") ||
        eventTarget === this.form.querySelector("variant-selects")
      );
    }

    extractVariantTerms(variant) {
      if (!variant) return "";

      return variant.metafields?.terms_and_conditions || "";
    }

    getSellingPlanSelector() {
      if (!this.form) return null;
      return this.form.querySelector("selling-plan-selector");
    }

    isOneTimeSelected() {
      const sellingPlanInput = this.form?.querySelector(
        'input[name="selling_plan"]',
      );

      // Selling plan input may not be present yet on initial custom element upgrade.
      if (!sellingPlanInput) return null;

      return !sellingPlanInput?.value;
    }

    getSelectedPlanDays() {
      const sellingPlanInput = this.form?.querySelector(
        'input[name="selling_plan"]',
      );
      if (!sellingPlanInput?.value) return "";

      const plansJson = document.querySelector(
        'script[id^="product-selling-plans-json-"]',
      );
      if (!plansJson) return "";

      let plans;
      try {
        plans = JSON.parse(plansJson.textContent);
      } catch (error) {
        console.error("Unable to parse selling plans JSON:", error);
        return "";
      }

      const selectedPlan = plans.find(
        (plan) => String(plan.id) === String(sellingPlanInput.value),
      );
      const frequencyOption = selectedPlan?.options?.find(
        (option) => option.name === "Order Frequency and Unit",
      );

      const frequencyValue = frequencyOption?.value || "";
      const localizedFrequencyValue =
        window.order_frequency_text?.[frequencyValue];

      return localizedFrequencyValue || frequencyValue;
    }

    updateInputValue() {
      if (!this.input) return;

      if (!this.template.includes("[days]")) {
        this.input.value = this.template;
        return;
      }

      const oneTimeSelected = this.isOneTimeSelected();

      if (oneTimeSelected === null) {
        // Keep server-rendered value and retry after the form DOM settles.
        if (this.initialSyncAttempts < 5) {
          this.initialSyncAttempts += 1;
          requestAnimationFrame(() => this.updateInputValue());
        }
        return;
      }

      if (oneTimeSelected) {
        this.input.value = "";
        return;
      }

      this.input.value = this.template.replace(
        /\[days\]/g,
        this.getSelectedPlanDays(),
      );
    }

    handleVariantChange(event) {
      if (!this.isVariantEventForThisForm(event.target)) return;

      const variantTerms = this.extractVariantTerms(event.data?.variant);

      if (variantTerms) {
        this.template = variantTerms;
      }

      this.updateInputValue();
    }

    handleSellingPlanChange(event) {
      if (!this.form) return;

      const eventFormId = event.data?.formId;
      if (eventFormId && this.form.id && eventFormId !== this.form.id) return;

      this.updateInputValue();
    }
  }

  customElements.define("subscription-terms-sync", SubscriptionTermsSync);
}

if (!customElements.get("klarna-price-info")) {
  class KlarmaPriceInfo extends HTMLElement {
    constructor() {
      super();
    }

    sellingPlanChangedUnsubscriber = undefined;

    connectedCallback() {
      this.sellingPlanChangedUnsubscriber = subscribe(
        PUB_SUB_EVENTS.sellingPlanUpdate,
        (event) => {
          if (this.dataset.display == "both") {
            this.style.display = "block";
          } else if (this.dataset.display == "one-time") {
            this.style.display = event.data.sellingPlan ? "none" : "block";
          } else {
            this.style.display = event.data.sellingPlan ? "block" : "none";
          }
        },
      );
    }

    disconnectedCallback() {
      if (this.sellingPlanChangedUnsubscriber) {
        this.sellingPlanChangedUnsubscriber();
      }
    }
  }

  customElements.define("klarna-price-info", KlarmaPriceInfo);
}

// This is used with the new recharge subscription selector.
//
(() => {
  const valueDescriptor = Object.getOwnPropertyDescriptor(
    HTMLInputElement.prototype,
    "value",
  );

  if (!valueDescriptor?.get || !valueDescriptor.set) return;

  const notifySellingPlanChange = (input, value) => {
    input.dispatchEvent(
      new CustomEvent("selling-plan:change", {
        bubbles: true,
        detail: { value },
      }),
    );
  };

  const observeSellingPlanInput = (input) => {
    if (input.dataset.sellingPlanObserved) return;

    input.dataset.sellingPlanObserved = "true";
    Object.defineProperty(input, "value", {
      configurable: true,
      enumerable: valueDescriptor.enumerable,
      get: valueDescriptor.get,
      set(value) {
        const previousValue = valueDescriptor.get.call(this);
        valueDescriptor.set.call(this, value);
        const nextValue = valueDescriptor.get.call(this);

        if (previousValue !== nextValue) {
          notifySellingPlanChange(this, nextValue);
        }
      },
    });
  };

  const observeSellingPlanInputs = (root = document) => {
    root
      .querySelectorAll('input[name="selling_plan"]')
      .forEach(observeSellingPlanInput);
  };

  observeSellingPlanInputs();
  new MutationObserver(() => observeSellingPlanInputs()).observe(
    document.body,
    {
      childList: true,
      subtree: true,
    },
  );

  document.addEventListener("selling-plan:change", (event) => {
    publish(PUB_SUB_EVENTS.sellingPlanUpdate, {
      data: {
        sellingPlan: event.detail.value,
        formId: event.target.form?.id || null,
      },
    });
  });
})();
